import React, { useState, useEffect } from 'react'
import { Box } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { connect } from 'react-redux'
import TermsForm from './TermsForm'
import Loader from 'components/shared/Loader'
import { getTermsData, getFCTermsDetailById, submitTermsData, sendToDocusign, getAllFactorCloudTerms, getAllDocusignTemplates } from 'actions/applicants'

import { Toast } from 'components/shared/Toaster'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    minHeight: '500px'
  }
}))

const Terms = ({ applicantId, getTermsData, getFCTermsDetailById, submitTermsData, sendToDocusign, getAllFactorCloudTerms, getAllDocusignTemplates }) => {
  const classes = useStyles()
  const [termData, setTermData] = useState(null)
  const [fCTermsDetail, setFCTermsDetail] = useState(null)
  const [termsList, setTermsList] = useState([])
  const [templatesList, setTemplatesList] = useState([])
  // The templates fetch is silent (a client without DocuSign must not toast on tab open), but a 409 means the
  // connection expired: that one has to be visible here, or the user only finds out on SEND DOCUSIGN.
  const [docusignAccessExpired, setDocusignAccessExpired] = useState(false)
  // Owned by fetchAll only. The individual fetchers used to set it too, and since they run concurrently
  // the first one to finish flipped it to false while the others were still in flight, unmounting and
  // remounting TermsForm (losing its local state, e.g. an open dialog) and painting empty dropdowns.
  const [loading, setLoading] = useState(true)
  const [loadingFCTermDetail, setLoadingFCTermDetail] = useState(false)
  const [formLoader, setFormLoader] = useState(false)

  const fetchTermsData = async () => {
    if (applicantId) {
      try {
        const response = await getTermsData(applicantId)
        setTermData(response)
        return response
      } catch (err) {
        console.error('Error fetching terms data:', err)
      }
    }
  }

    const fetchTermsDataByTermId = async (termId) => {
      try {
        setLoadingFCTermDetail(true)
        const response = await getFCTermsDetailById(termId)
        setFCTermsDetail(response)
      } catch (err) {
        console.error('Error fetching terms data:', err)
      } finally {
        setLoadingFCTermDetail(false)
      }
  }

  const fetchTermsList = async () => {
    try {
      const response = await getAllFactorCloudTerms()
      setTermsList(response)
    } catch (err) {
      console.error('Error fetching terms options:', err)
    }
  }

  const fetchTemplatesList = async () => {
    try {
      const response = await getAllDocusignTemplates()
      setTemplatesList(response)
      setDocusignAccessExpired(false)
    } catch (err) {
      console.error('Error fetching templates options:', err)
      setDocusignAccessExpired(err?.status === 409)
    }
  }


  // termData goes first because it carries the flag that says whether the FactorCloud list is worth fetching.
  // The lists are awaited so callers of fetchAll (save, then send to DocuSign) do not resume while the tab
  // is still filling its dropdowns.
  const fetchAll = async () => {
    setLoading(true)
    try {
      const terms = await fetchTermsData()
      if (!terms?.factorCloudAvailable) setTermsList([])
      await Promise.all([
        fetchTemplatesList(),
        terms?.factorCloudAvailable ? fetchTermsList() : null,
      ])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  // fetchAll is recreated every render, so listing it here would re-run the effect on every render and
  // loop the requests. Every value it reads is already covered by the dependencies below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantId, getTermsData, getAllFactorCloudTerms, getAllDocusignTemplates])

  // Returns whether the save went through, so callers that chain on it (send to DocuSign with pending
  // changes) do not act on a failed save.
  const handleFormSubmit = async (formData) => {
    try {
      setFormLoader(true)
      await submitTermsData(applicantId, formData)
      Toast({
        type: 'success',
        message: 'Terms have been submitted successfully!'
      })
      await fetchAll()
      return true
    } catch (err) {
      return false
    } finally {
      setFormLoader(false)
    }
  }

    const callToSendToDocusign = async () => {
      return sendToDocusign(applicantId)
    }
  

  if (loading) {
    return (
      <Box className={classes.container}>
        <Loader />
      </Box>
    )
  }

  return (
    <Box className={classes.container}>
      <TermsForm
        termsList={termsList}
        templatesList={templatesList}
        docusignAccessExpired={docusignAccessExpired}
        termData={termData}
        fCTermsDetail={fCTermsDetail}
        loadingFCTermDetail={loadingFCTermDetail}
        onSubmit={handleFormSubmit}
        sendToDocusign={callToSendToDocusign}
        formLoader={formLoader}
        fetchTermsData={fetchTermsData}
        fetchTermsDataByTermId={fetchTermsDataByTermId}
        fetchTemplatesList={fetchTemplatesList}
        fetchTermsList={fetchTermsList}
      />
    </Box>
  )
}

const mapDispatchToProps = {
  getTermsData,
  getFCTermsDetailById,
  submitTermsData,
  sendToDocusign,
  getAllFactorCloudTerms,
  getAllDocusignTemplates
}

export default connect(null, mapDispatchToProps)(Terms)